import { Position } from '../types/api.v2.types';

const ENABLE_PRINT_DITHER = true;
const BRIGHT_DITHER_DENSITY = 10;
const MID_DITHER_DENSITY = 16;
const DARK_DITHER_DENSITY = 12;

const applyHighlightDither = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
) => {
  if (!ENABLE_PRINT_DITHER) {
    return;
  }

  const imageData = ctx.getImageData(0, 0, width, height);
  const data = imageData.data;
  const source = new Uint8ClampedArray(data);

  const getIndex = (x: number, y: number) => (y * width + x) * 4;

  // Pass 1: flatten low-frequency waves in low-saturation backgrounds.
  for (let y = 0; y < height - 1; y++) {
    for (let x = 0; x < width - 1; x++) {
      const index = getIndex(x, y);
      const r = source[index];
      const g = source[index + 1];
      const b = source[index + 2];
      const a = source[index + 3];

      if (a < 250) {
        continue;
      }

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      const saturation = max === 0 ? 0 : (max - min) / max;

      if (saturation > 0.1 || luminance < 150 || luminance > 250) {
        continue;
      }

      const right = getIndex(x + 1, y);
      const bottom = getIndex(x, y + 1);
      const edgeStrength =
        Math.abs(source[right] - r) +
        Math.abs(source[right + 1] - g) +
        Math.abs(source[right + 2] - b) +
        Math.abs(source[bottom] - r) +
        Math.abs(source[bottom + 1] - g) +
        Math.abs(source[bottom + 2] - b);

      if (edgeStrength > 16) {
        continue;
      }

      const diagonal = getIndex(x + 1, y + 1);
      const avgR = Math.round((r + source[right] + source[bottom] + source[diagonal]) / 4);
      const avgG = Math.round((g + source[right + 1] + source[bottom + 1] + source[diagonal + 1]) / 4);
      const avgB = Math.round((b + source[right + 2] + source[bottom + 2] + source[diagonal + 2]) / 4);

      const flattenStrength = luminance >= 220 ? 0.28 : 0.4;
      data[index] = Math.round(r * (1 - flattenStrength) + avgR * flattenStrength);
      data[index + 1] = Math.round(g * (1 - flattenStrength) + avgG * flattenStrength);
      data[index + 2] = Math.round(b * (1 - flattenStrength) + avgB * flattenStrength);
    }
  }

  // Pass 2: small blue-noise style dithering.
  for (let y = 0; y < height - 1; y++) {
    for (let x = 0; x < width - 1; x++) {
      const index = getIndex(x, y);
      const r = data[index];
      const g = data[index + 1];
      const b = data[index + 2];
      const a = data[index + 3];

      if (a < 250) {
        continue;
      }

      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
      const saturation = max === 0 ? 0 : (max - min) / max;

      // Low-saturation backgrounds only.
      if (saturation > 0.1) {
        continue;
      }

      // Skip edges/details so faces, text, and contours are less affected.
      const right = getIndex(x + 1, y);
      const bottom = getIndex(x, y + 1);
      const edgeStrength =
        Math.abs(data[right] - r) +
        Math.abs(data[right + 1] - g) +
        Math.abs(data[right + 2] - b) +
        Math.abs(data[bottom] - r) +
        Math.abs(data[bottom + 1] - g) +
        Math.abs(data[bottom + 2] - b);

      if (edgeStrength > 18) {
        continue;
      }

      // Deterministic high-frequency mask with luminance-banded density.
      const mask = ((x * 73856093) ^ (y * 19349663)) & 255;
      let density = 0;
      if (luminance >= 242) {
        density = BRIGHT_DITHER_DENSITY;
      } else if (luminance >= 205) {
        density = MID_DITHER_DENSITY;
      } else if (luminance >= 165) {
        density = DARK_DITHER_DENSITY;
      }

      if (density === 0 || mask >= density) {
        continue;
      }

      const sign = (mask & 1) === 0 ? -1 : 1;
      data[index] = Math.max(0, Math.min(255, r + sign));
      data[index + 1] = Math.max(0, Math.min(255, g + sign));
      data[index + 2] = Math.max(0, Math.min(255, b + sign));
    }
  }

  ctx.putImageData(imageData, 0, 0);
};

/**
 * 이미지를 좌우 반전시키는 함수
 */
export const flipImageHorizontally = async (imageDataUrl: string): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Canvas context is not supported'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;

      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
      ctx.drawImage(img, 0, 0);

      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageDataUrl;
  });
};

export const createFourCutImage = async (
  imageDatas: string[],
  backgroundImage: string,
  printPhotoPositions: Position[]
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      reject(new Error('Canvas context is not supported'));
      return;
    }

    const baseWidth = 1152;
    const baseHeight = 1728;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = baseWidth * dpr;
    canvas.height = baseHeight * dpr;

    ctx.scale(dpr, dpr);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const loadImages = () => {
      let loadedImages = 0;

      imageDatas.forEach((imageData, index) => {
        const img = new Image();
        img.onload = () => {
          const position = printPhotoPositions[index];
          ctx.drawImage(img, position.left, position.top, 544, 680);
          loadedImages++;

          if (loadedImages === 4) {
            const background = new Image();
            background.crossOrigin = 'anonymous';
            background.onload = () => {
              ctx.drawImage(background, 0, 0, baseWidth, baseHeight);
              applyHighlightDither(ctx, canvas.width, canvas.height);

              const dataUrl = canvas.toDataURL('image/png', 1.0);
              resolve(dataUrl + '#dpr=' + dpr);
            };
            background.onerror = () => {
              reject(new Error('Failed to load background image'));
            };
            background.src = backgroundImage;
          }
        };
        img.onerror = () => reject(new Error(`Failed to load image ${index + 1}`));
        img.src = imageData;
      });
    };

    loadImages();
  });
};

export const createImageFile = async (dataUrl: string, fileName: string) => {
  const cleanDataUrl = dataUrl.split('#')[0];
  const blob = await fetch(cleanDataUrl).then((res) => res.blob());
  return new File([blob], fileName, { type: 'image/png' });
};

export const createVideoFiles = async (videos: string[]) => {
  return Promise.all(
    videos.map(async (video, index) => {
      const blob = await fetch(video).then((r) => r.blob());
      return new File([blob], `video${index + 1}.mp4`, { type: 'video/mp4' });
    })
  );
};

export const ensureImageSize = async (
  imageFile: File,
  targetWidth: number,
  targetHeight: number
): Promise<File> => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext('2d');

      if (ctx) {
        const imgRatio = img.width / img.height;
        const targetRatio = targetWidth / targetHeight;

        let sw: number, sh: number, sx: number, sy: number;

        if (imgRatio > targetRatio) {
          sh = img.height;
          sw = sh * targetRatio;
          sx = (img.width - sw) / 2;
          sy = 0;
        } else {
          sw = img.width;
          sh = sw / targetRatio;
          sx = 0;
          sy = (img.height - sh) / 2;
        }

        ctx.clearRect(0, 0, targetWidth, targetHeight);
        ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);
      }

      canvas.toBlob((blob) => {
        if (blob) {
          resolve(
            new File([blob], imageFile.name.replace(/\.[^/.]+$/, '.png'), {
              type: 'image/png',
            })
          );
        } else {
          resolve(imageFile);
        }
      }, 'image/png');
    };
    img.src = URL.createObjectURL(imageFile);
  });
};

export const getFourCutImageWithQrCode = (
  fourCutImage: string,
  qrCode: string,
  qrCodePosition: {
    top: number;
    left: number;
  }
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      reject(new Error('context is not available'));
      return;
    }

    let dpr = 1;
    const parts = fourCutImage.split('#dpr=');
    let imgSrc = fourCutImage;

    if (parts.length > 1) {
      imgSrc = parts[0];
      dpr = parseFloat(parts[1]) || 1;
    }

    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);

      const qrImg = new Image();
      qrImg.onload = () => {
        const adjustedLeft = qrCodePosition.left * dpr;
        const adjustedTop = qrCodePosition.top * dpr;
        const adjustedSize = 120 * dpr;

        ctx.drawImage(qrImg, adjustedLeft, adjustedTop, adjustedSize, adjustedSize);

        const fourCutImageWithQrCode = canvas.toDataURL('image/png');
        resolve(fourCutImageWithQrCode);
      };
      qrImg.onerror = () => reject(new Error('Failed to load QR code image'));
      qrImg.src = qrCode;
    };
    img.onerror = () => reject(new Error('Failed to load four cut image'));
    img.src = imgSrc;
  });
};
